// Mobile Feed Wrapper Component
// Wraps the social feed with mobile-specific features and bottom navigation

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MobileFeedContainer } from "../../styles/SocialFeed/BottomNavigation.styles";
import { FeedContainer } from "./FeedContainer/FeedContainer";
import { useAuthContext } from "../../providers";
import { logger } from '../../utils/logger';
import { config } from "../../config/env";

interface MobileFeedWrapperProps {
  userId: string;
  onItemInteraction?: (interaction: any) => void;
  onSwipeModeToggle?: (enabled: boolean) => void;
  className?: string;
}

export const MobileFeedWrapper: React.FC<MobileFeedWrapperProps> = ({
  userId,
  onItemInteraction,
  onSwipeModeToggle,
  className,
}) => {
  const location = useLocation();
  // Shared auth state, not a private useAuth() copy — see the note in
  // SocialFeed.tsx. A second instance here re-ran the whole session check and,
  // because the guard below ignored its `loading` flag, painted "please log in"
  // for the duration of it on every refresh.
  const { user, loading } = useAuthContext();
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const [isSwipeModeActive, setIsSwipeModeActive] = useState(false);

  // Debug logging removed for cleaner console

  // Hide bottom navigation on certain pages or when swipe mode is active
  useEffect(() => {
    const hideNavPages = ['/profile/setup'];
    const shouldHide = hideNavPages.some(page => location.pathname.includes(page)) || isSwipeModeActive;
    setIsBottomNavVisible(!shouldHide);
  }, [location.pathname, isSwipeModeActive]);

  // Handle swipe mode toggle
  const handleSwipeModeToggle = (enabled: boolean) => {
    setIsSwipeModeActive(enabled);
    onSwipeModeToggle?.(enabled);
  };

  // `loading` is load-bearing here: "no user yet" and "not logged in" are not
  // the same state, and treating them as one is what showed a logged-out
  // message to a logged-in user for the length of the auth check on refresh.
  // In mock credentials mode, allow access even without user (user can explore)
  // The userId prop is passed from parent, so we can still render the feed
  if (!user && !loading && !config.dev.mockCredentials) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        color: "white"
      }}>
        Please log in to view your social feed.
      </div>
    );
  }

  return (
    <>
      {/* Mobile Feed Container */}
      <MobileFeedContainer className={className}>
        <FeedContainer
          userId={userId}
          onItemInteraction={onItemInteraction}
          onSwipeModeToggle={handleSwipeModeToggle}
        />
      </MobileFeedContainer>
    </>
  );
};
